import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Box, Typography } from '@mui/material';
import getVoyageStatusChip, {
  getVoyageStatusLabel,
  VOYAGE_STATUS_OPTIONS,
} from '../../sales/components/voyage/VoyageStatusUtils';

dayjs.extend(isoWeek);

const makeSafeDateFormatter =
  (format = 'DD.MM.YYYY') =>
  (params) => {
    const value = params?.value ?? params;
    if (value === null || value === undefined || value === '') return '';
    const d = dayjs(value);
    if (!d.isValid()) return '';
    return d.format(format);
  };

/** Departure Delay tile: ≤1 → green, ≥2 → red, null → no color */
const renderDepartureDelay = (params) => {
  const value = params.value;
  if (value == null)
    return (
      <Box
        display='flex'
        alignItems='center'
        width='100%'
        height='100%'
        sx={{ padding: '0 10px' }}
      >
        <Typography variant='body2' sx={{ width: '100%', textAlign: 'right' }}>
          —
        </Typography>
      </Box>
    );
  const bg =
    value <= 1
      ? (t) =>
          t.palette.mode === 'dark'
            ? 'rgba(46, 125, 50, 0.35)'
            : 'rgba(46, 125, 50, 0.22)'
      : (t) =>
          t.palette.mode === 'dark'
            ? 'rgba(211, 47, 47, 0.35)'
            : 'rgba(211, 47, 47, 0.22)';
  const color = value <= 1 ? 'success.main' : 'error.main';
  return (
    <Box
      display='flex'
      alignItems='center'
      width='100%'
      height='100%'
      sx={{ padding: '0 10px', backgroundColor: bg }}
    >
      <Typography
        variant='body2'
        fontWeight='medium'
        sx={{ color, width: '100%', textAlign: 'right' }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/** Sales Day tile: ETA day ≤ salesDay → green, salesDay+1 → yellow, later → red */
const renderSalesDay = (params) => {
  const salesDay = params.value;
  const actualEta = params.row.actualEta ?? params.row.actualETA ?? null;
  if (salesDay == null || actualEta == null)
    return (
      <Box
        display='flex'
        alignItems='center'
        width='100%'
        height='100%'
        sx={{ padding: '0 10px' }}
      >
        <Typography variant='body2' sx={{ width: '100%', textAlign: 'right' }}>
          {salesDay ?? '—'}
        </Typography>
      </Box>
    );
  const etaDate = dayjs(actualEta);
  if (!etaDate.isValid())
    return (
      <Box
        display='flex'
        alignItems='center'
        width='100%'
        height='100%'
        sx={{ padding: '0 10px' }}
      >
        <Typography variant='body2' sx={{ width: '100%', textAlign: 'right' }}>
          —
        </Typography>
      </Box>
    );
  const etaDay = etaDate.date();
  let bg, color;
  if (etaDay <= salesDay) {
    bg = (t) =>
      t.palette.mode === 'dark'
        ? 'rgba(46, 125, 50, 0.35)'
        : 'rgba(46, 125, 50, 0.22)';
    color = 'success.main';
  } else if (etaDay === salesDay + 1) {
    bg = (t) =>
      t.palette.mode === 'dark'
        ? 'rgba(237, 108, 2, 0.35)'
        : 'rgba(237, 108, 2, 0.22)';
    color = 'warning.main';
  } else {
    bg = (t) =>
      t.palette.mode === 'dark'
        ? 'rgba(211, 47, 47, 0.35)'
        : 'rgba(211, 47, 47, 0.22)';
    color = 'error.main';
  }
  return (
    <Box
      display='flex'
      alignItems='center'
      width='100%'
      height='100%'
      sx={{ padding: '0 10px', backgroundColor: bg }}
    >
      <Typography
        variant='body2'
        fontWeight='medium'
        sx={{ color, width: '100%', textAlign: 'right' }}
      >
        {salesDay}
      </Typography>
    </Box>
  );
};

const columnsDefinitions = ({
  extraColumns = undefined,
  dateFormat = 'DD.MM.YYYY',
} = {}) => {
  const safeDateFormatter = makeSafeDateFormatter(dateFormat);
  return [
    {
      field: 'initialEtd',
      headerName: 'Initial ETD',
      width: 200,
      type: 'date',
      valueFormatter: safeDateFormatter,
    },
    {
      field: 'actualEtd',
      headerName: 'Actual ETD',
      width: 200,
      type: 'date',
      valueFormatter: safeDateFormatter,
    },
    {
      field: 'calendarWeek',
      headerName: 'CW',
      description: 'Calendar Week',
      width: 80,
      valueGetter: (_value, row) => {
        const date = row.actualEtd ?? row.initialEtd;
        if (date == null) return '';
        const d = dayjs(date);
        return d.isValid() ? d.isoWeek() : '';
      },
    },
    {
      field: 'departureDelay',
      headerName: 'Departure Delay',
      width: 150,
      type: 'number',
      renderCell: renderDepartureDelay,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      type: 'singleSelect',
      valueOptions: VOYAGE_STATUS_OPTIONS,
      valueGetter: (value, row) => {
        const actualEtd = row.actualEtd ?? row.actualETD ?? null;
        const actualEta = row.actualEta ?? row.actualETA ?? null;
        const cancelled = row.cancelled ?? false;
        return getVoyageStatusLabel(
          actualEtd,
          actualEta,
          cancelled,
          row.effectiveShipped
        );
      },
      renderCell: (params) => {
        const actualEtd = params.row.actualEtd ?? params.row.actualETD ?? null;
        const actualEta = params.row.actualEta ?? params.row.actualETA ?? null;
        const cancelled = params.row.cancelled ?? false;
        return getVoyageStatusChip(
          actualEtd,
          actualEta,
          cancelled,
          params.row.effectiveShipped
        );
      },
    },
    { field: 'shipImo', headerName: 'Ship IMO', width: 150 },
    { field: 'shipName', headerName: 'Ship Name', width: 150 },
    { field: 'carrierId', headerName: 'Shipping Company ID', width: 150 },
    { field: 'suppliername', headerName: 'Shipping Company', width: 150 },
    {
      field: 'arrivalDelay',
      headerName: 'Arrival Delay',
      width: 150,
      type: 'number',
    },
    {
      field: 'initialEta',
      headerName: 'Initial ETA',
      width: 200,
      type: 'date',
      valueFormatter: safeDateFormatter,
    },
    {
      field: 'actualEta',
      headerName: 'Actual ETA',
      width: 200,
      type: 'date',
      valueFormatter: safeDateFormatter,
    },
    {
      field: 'duration',
      headerName: 'Transit Time',
      width: 150,
      type: 'number',
    },
    { field: 'marketCountryName', headerName: 'Market Name', width: 150 },
    { field: 'destinationPort', headerName: 'Destination Port', width: 150 },
    {
      field: 'destinationPortName',
      headerName: 'Destination Port Name',
      width: 150,
    },
    {
      field: 'destinationCountry',
      headerName: 'Destination Country',
      width: 150,
    },
    {
      field: 'currentStock',
      headerName: 'Current Stock',
      width: 150,
      type: 'number',
    },
    {
      field: 'readyForShipment',
      headerName: 'Ready for Shipment',
      width: 150,
      type: 'number',
    },
    {
      field: 'totalBlockedCars',
      headerName: 'Blocked',
      width: 150,
      type: 'number',
    },
    {
      field: 'forecastInflow',
      headerName: 'Forecast Inflow',
      width: 150,
      type: 'number',
    },
    {
      field: 'actualInflow',
      headerName: 'Actual Inflow',
      width: 150,
      type: 'number',
    },
    {
      field: 'firstShippingLineOffer',
      headerName: 'First Shipping Line Offer',
      width: 200,
      type: 'number',
      filterable: false,
    },
    {
      field: 'adjustedPoRequest',
      headerName: 'Adjusted PO Request',
      width: 200,
      type: 'number',
      filterable: false,
    },
    {
      field: 'finalAgreement',
      headerName: 'Final Agreement',
      width: 150,
      type: 'number',
      filterable: false,
    },
    {
      field: 'effectiveShipped',
      headerName: 'Effective Shipped',
      width: 150,
      type: 'number',
      filterable: false,
    },
    {
      field: 'slotConfirmed',
      headerName: 'Slot Confirmed',
      width: 150,
      type: 'boolean',
    },
    { field: 'generalRemarks', headerName: 'General Remarks', width: 300 },
    {
      field: 'cancelled',
      headerName: 'Cancelled',
      width: 150,
      type: 'boolean',
    },
    { field: 'voyageNumber', headerName: 'Voyage Number', width: 150 },
    {
      field: 'voyageChanged',
      headerName: 'Voyage Changed',
      width: 150,
      type: 'boolean',
    },
    { field: 'shipSupplier', headerName: 'Ship Supplier', width: 150 },
    { field: 'shipIrs', headerName: 'Ship IRS', width: 150 },
    {
      field: 'shipCapacity',
      headerName: 'Ship Capacity',
      width: 150,
      type: 'number',
    },
    {
      field: 'shipmentChanged',
      headerName: 'Shipment Changed',
      width: 150,
      type: 'boolean',
    },
    { field: 'voyageId', headerName: 'Voyage ID', width: 200 },
    { field: 'portOperatorId', headerName: 'Port Operator ID', width: 150 },
    {
      field: 'voyageLastUpdatedAt',
      headerName: 'Voyage Last Updated At',
      width: 200,
    },
    {
      field: 'salesDay',
      headerName: 'Sales Day',
      width: 150,
      type: 'number',
      renderCell: renderSalesDay,
    },
    { field: 'departurePort', headerName: 'Departure Port', width: 150 },
    {
      field: 'departurePortName',
      headerName: 'Departure Port Name',
      width: 150,
    },
    { field: 'departureCountry', headerName: 'Departure Country', width: 150 },
    ...(extraColumns || []),
  ];
};

export default columnsDefinitions;
